import { Component, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Models } from '@ibexfusion/models';
import { MatDialog } from '@angular/material/dialog';
import { VideoAssetsService } from '@services/assets/video-assets.service';
import { ImageAssetsService } from '@services/assets/image-assets.service';
import { EnvironmentService } from '@services/environment/environment.service';
import { IbexCheckboxComponent } from '../../../../../components/ibex-checkbox/ibex-checkbox.component';
import { SelectAssetsComponent } from '../../../../../components/select-assets/select-assets.component';
import { DialogService } from '@services/dialog/dialog.service';
import { TranslateService } from '@services/translate/translate.service';
import { EditMediaComponent } from '../edit-media/edit-media.component';
import { VideoAssetsPropertiesService } from '@services/video-assets/video-assets-properties.service';
import { EditAssetComponent } from '../edit-asset/edit-asset.component';

@Component({
  selector: 'media',
  templateUrl: './media.component.html',
  styleUrls: ['./media.component.scss'],
  providers: [ImageAssetsService, VideoAssetsService, VideoAssetsPropertiesService],
  standalone: false
})
export class MediaComponent implements OnInit {

  @Input() dynamicContent;
  @Input() imagesOnly = false;
  @Input() imageOnly = false;
  @Input() availableLanguages = [];
  @Input() packageLang;
  @Input() isSecured = false;
  @Input() parents = '';
  @Input() parentId = '';
  @ViewChild('checkboxAll') checkboxAll: IbexCheckboxComponent;
  @ViewChildren(IbexCheckboxComponent) checkboxes!: QueryList<IbexCheckboxComponent>;
  public imageAssetPath: any;
  public videoPath: any;
  public filesByPath = {};
  public timestamp = Date.now();
  selectedFiles = [];
  checkBoxActions = [{
    translation: 'dynamicContent.disconnectMultipleItems', icon: 'delete', action: () => {
      this.disconnectSelectedItems()
    }
  }];

  constructor(
    private dialog: MatDialog, private imageAssetsService: ImageAssetsService, private videoAssetsService: VideoAssetsService,
    private videoAssetsPropertiesService: VideoAssetsPropertiesService,
    private environmentService: EnvironmentService, private dialogService: DialogService, private ts: TranslateService
  ) {
    this.imageAssetPath = this.environmentService.getImageAssetPath();
    this.videoPath = this.environmentService.getVideoAssetPath();
  }

  async ngOnInit() {
    const paths = [];
    if (this.dynamicContent) {
      for (const mediaFile of this.dynamicContent.mediaFiles) {
        paths.push(mediaFile.url);
      }
      const images = (await this.imageAssetsService.find(0, paths.length, { path: { $in: paths } })).data as any[];
      const videos = (await this.videoAssetsPropertiesService.find(0, paths.length, { path: { $in: paths } })).data as any[];
      for (const image of images.concat(videos)) {
        this.filesByPath[image.path] = image;
      }
    }
  }

  public async chooseFromImageAssets(): Promise<any> {
    const assets: Models.ImageAsset[] = await this.dialog.open(SelectAssetsComponent, {
      width: '880px', height: '75%', panelClass: 'select-assets-modal', data: {
        type: 'image', multiple: !this.imageOnly, title: 'Bilder', dynamicContent: this.dynamicContent,
        isSecured: this.isSecured, parent: this.parents, parentId: this.parentId
      }
    }).afterClosed().toPromise();
    this.addAssets(assets);
  }

  public async chooseFromVideoAssets(): Promise<any> {
    const assets: Models.ImageAsset[] = await this.dialog.open(SelectAssetsComponent, {
      width: '800px', height: '75%', panelClass: 'select-assets-modal',
      data: { type: 'video', multiple: true, title: 'Videos', parent: this.parents, parentId: this.parentId }
    }).afterClosed().toPromise();
    this.addAssets(assets);
  }

  public async disconnectImage(index) {
    const msg = this.ts.get('dynamicContent.deleteSelectedQuestion');
    this.dialogService.confirm(msg).then(res => {
      if (!res) {
        return;
      }
      this.dynamicContent.mediaFiles.splice(index, 1);
    });
  }

  public async editMedia(asset) {
    let boundImageAsset;
    if (asset.resizeInformation) {
      boundImageAsset = await this.imageAssetsService.get(asset._id);
    }
    await this.dialog.open(EditMediaComponent, {
      width: '875px', height: 'auto', panelClass: 'edit-media-modal', data: {
        asset: boundImageAsset, availableLanguages: this.availableLanguages, packageLang: this.packageLang,
        dataType: this.dynamicContent.type, isInContent: true, page: 'content'
      }
    }).afterClosed().toPromise();
    try {
      const res = await this.imageAssetsService.get(asset._id);
      this.filesByPath[res.path] = res;
    } catch (err) {
      console.warn(err);
    }
  }

  public async editAsset(asset) {
    let boundAsset = await this.videoAssetsService.get(asset._id);
    await this.dialog.open(EditAssetComponent, {
      width: '875px', height: 'auto', panelClass: 'edit-asset-modal', data: {
        asset: boundAsset, availableLanguages: this.availableLanguages, packageLang: this.packageLang,
        dataType: this.dynamicContent.type, isInContent: true, page: 'content'
      }
    }).afterClosed().toPromise();
    try {
      const res = await this.videoAssetsService.get(asset._id);
      this.filesByPath[res.path] = res;
    } catch (err) {
      console.warn(err);
    }
  }

  public saveMedia(asset) {
    if (asset.mimeType.indexOf('image') !== -1) {
      this.imageAssetsService.update(asset._id, asset).then((res) => {
        this.filesByPath[asset.path] = res;
      });
    } else if (asset.mimeType.indexOf('video') !== -1) {
      this.videoAssetsPropertiesService.update(asset._id, asset).then((res) => {
        this.filesByPath[asset.path] = res;
      });
    }
  }

  public disconnectSelectedItems() {
    for (const file of this.selectedFiles) {
      this.dynamicContent.mediaFiles.splice(this.dynamicContent.mediaFiles.findIndex(el => el.url === file.url), 1);
    }
    this.selectedFiles = [];
    this.checkboxes.forEach(checkbox => {
      checkbox.unselect();
    });
  }

  public dropImage(event: any) {
    if (event.previousIndex !== event.currentIndex) {
      const item = this.dynamicContent.mediaFiles.splice(event.previousIndex, 1)[0];
      this.dynamicContent.mediaFiles.splice(event.currentIndex, 0, item);
    }
  }

  public selectItem(file: any) {
    if (this.selectedFiles.map(el => el.url).includes(file.url)) {
      this.selectedFiles.splice(this.selectedFiles.map(el => el.url).indexOf(file.url), 1);
    } else {
      this.selectedFiles.push(file);
    }
    if (this.selectedFiles?.length === this.dynamicContent?.mediaFiles?.length) {
      this.checkboxAll.select();
    } else {
      this.checkboxAll.unselect();
    }
  }

  public selectItemsAll() {
    if (this.selectedFiles?.length === this.dynamicContent?.mediaFiles?.length) {
      this.selectedFiles = [];
    } else {
      this.selectedFiles = JSON.parse(JSON.stringify(this.dynamicContent.mediaFiles));
    }
    this.checkboxes.filter(el => el.id !== 'checkboxAll').forEach(checkbox => {
      if (this.selectedFiles.length === 0) {
        checkbox.unselect();
      } else {
        checkbox.select();
      }
    });
  }

  private addAssets(assets) {
    if (!assets) {
      return;
    } else if (this.imageOnly) {
      this.processAsset(assets?.[0] || assets, true);
    } else if (assets.length) {
      assets.forEach(asset => this.processAsset(asset));
    }
  }

  private processAsset(asset: any, singleImage = false) {
    const item = new Models.FileObject();
    Object.assign(item, { name: asset.originalFileName, mimeType: asset.mimeType, url: asset.path });
    !!singleImage ? this.filesByPath = { [asset.path]: asset } : this.filesByPath[asset.path] = asset;
    !!singleImage ? this.dynamicContent.mediaFiles = [item] : this.dynamicContent.mediaFiles.push(item);
  }

  hover(item, event: any) {
    const imageContainer = document.createElement('div');
    const containerWidth = 500;
    const containerHeight = 500;
    Object.assign(imageContainer.style, {
      position: 'absolute', width: `${containerWidth}px`, height: `${containerHeight}px`, zIndex: '1000', pointerEvents: 'none', display: 'flex', justifyContent: 'center',
      alignItems: 'center', left: `${event.clientX - containerWidth / 2}px`, top: `${Math.max(10, event.clientY - containerHeight)}px`
    });
    imageContainer.id = 'media-img-preview-overlay'

    const wrapper = document.createElement('div');
    Object.assign(wrapper.style, { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' });

    imageContainer.appendChild(wrapper);

    const img = document.createElement('img');
    img.src = this.imageAssetPath + item.url + '/freeForm';
    img.alt = 'Dynamic Image';
    Object.assign(img.style, {
      maxWidth: '100%', maxHeight: '100%', display: 'flex', flexDirection: 'row', justifySelf: 'center', border: '5px solid white', borderRadius: '5px',
      boxShadow: 'rgba(0, 0, 0, 0.75) 0px 0px 10px'
    });
    wrapper.appendChild(img);
    document.body.appendChild(imageContainer);
  }

  leave() {
    document.getElementById('media-img-preview-overlay')?.remove();
  }

}
